import { Component, ErrorInfo, ReactNode } from 'react'
import { parseError, ERROR_CONFIG, AppError } from './errorTypes'
import Button from '@/components/Button'
import DoubleSidedImage from '@/components/shared/DoubleSidedImage'
import { useNavigate, type NavigateFunction } from 'react-router-dom'

interface ErrorBoundaryProps {
    children: ReactNode
    fallback?: ReactNode
    onError?: (error: Error, errorInfo: ErrorInfo) => void
    context?: string
    // Optional custom images per status code (use public/ paths for offline safety)
    errorImages?: Partial<
        Record<
            400 | 401 | 404 | 500,
            {
                src: string
                darkSrc?: string
            }
        >
    >
    // Custom navigation options
    primaryActionPath?: string
    primaryActionLabel?: string
    secondaryActionPath?: string
    secondaryActionLabel?: string
    showGoBack?: boolean
}

interface ErrorBoundaryState {
    hasError: boolean
    error: Error | null
    appError: AppError | null
}

interface ErrorBoundaryInnerProps extends ErrorBoundaryProps {
    navigate: NavigateFunction
}

class ErrorBoundaryInner extends Component<
    ErrorBoundaryInnerProps,
    ErrorBoundaryState
> {
    public state: ErrorBoundaryState = {
        hasError: false,
        error: null,
        appError: null,
    }

    public static getDerivedStateFromError(
        error: Error,
    ): Partial<ErrorBoundaryState> {
        return { hasError: true, error }
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Parse the error to understand its type
        const appError = parseError(error, this.props.context)

        // Update state with parsed error
        this.setState({ appError })

        // Call custom error handler if provided
        this.props.onError?.(error, errorInfo)
    }

    private handleRetry = () => {
        // Reset error state to retry
        this.setState({
            hasError: false,
            error: null,
            appError: null,
        })
    }

    private handleGoBack = () => {
        this.props.navigate(-1)
    }

    private handleGoHome = () => {
        this.props.navigate('/home')
    }

    private handleRefresh = () => {
        this.props.navigate(0)
    }

    private getImageSrc = (statusCode: number) => {
        // Prefer custom image override if provided
        const override =
            this.props.errorImages?.[statusCode as 400 | 401 | 404 | 500]
        if (override) {
            return {
                src: override.src,
                darkSrc: override.darkSrc || override.src,
            }
        }
        switch (statusCode) {
            case 400:
                return {
                    src: '/img/others/img-other-errors.png',
                    darkSrc: '/img/others/img-other-errors.png',
                }
            case 401:
                return {
                    src: '/img/others/img-other-errors.png',
                    darkSrc: '/img/others/img-other-errors.png',
                }
            case 404:
                return {
                    src: '/img/others/img-2.png',
                    darkSrc: '/img/others/img-2-dark.png',
                }
            case 500:
            default:
                return {
                    src: '/img/others/img-other-errors.png',
                    darkSrc: '/img/others/img-other-errors.png',
                }
        }
    }

    private getStatusColor = (statusCode: number) => {
        switch (statusCode) {
            case 400:
                return 'text-orange-600 dark:text-orange-400'
            case 401:
                return 'text-yellow-600 dark:text-yellow-400'
            case 404:
                return 'text-gray-900 dark:text-white'
            case 500:
            default:
                return 'text-red-600 dark:text-red-400'
        }
    }

    private handleLogin = () => {
        this.props.navigate('/sign-in')
    }

    private handlePrimaryAction = () => {
        const { primaryActionPath } = this.props

        if (primaryActionPath) {
            this.props.navigate(primaryActionPath)
        } else if (
            this.state.appError?.type === 'server' ||
            this.state.appError?.type === 'network'
        ) {
            this.handleRetry()
        } else if (this.state.appError?.type === 'auth') {
            this.handleLogin()
        } else {
            this.handleGoHome()
        }
    }

    private handleSecondaryAction = () => {
        const { secondaryActionPath } = this.props

        if (secondaryActionPath) {
            this.props.navigate(secondaryActionPath)
        } else {
            this.handleLogin()
        }
    }

    private renderErrorContent = () => {
        const { appError, error } = this.state
        const {
            primaryActionLabel,
            secondaryActionLabel,
            showGoBack = false,
        } = this.props

        // If no appError exists, return null
        if (!appError) return null

        // Use the caught error's statusCode
        const statusCode = appError?.statusCode || 500

        // Get default config based on error type or use server error as fallback
        const config = ERROR_CONFIG[appError.type]
        const imageSrc = this.getImageSrc(statusCode)

        // For 500 errors, always show the fixed message instead of actual description
        const displayMessage =
            statusCode === 500
                ? 'An internal server error occurred. Please try again.'
                : appError.message || error?.message || config.description

        return (
            <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 flex flex-col items-center justify-center p-6">
                <DoubleSidedImage
                    src={imageSrc.src}
                    darkModeSrc={imageSrc.darkSrc}
                    alt={`Error ${statusCode}`}
                />

                <h1
                    className={`text-4xl font-bold ${this.getStatusColor(
                        statusCode,
                    )} text-center mt-6`}
                >
                    {config.title || 'An Error Occurred'}
                </h1>

                {/* Display the error message (fixed for 500) */}
                <p className="text-gray-600 dark:text-gray-400 text-center mt-4 max-w-md">
                    {displayMessage}
                </p>

                <div className="mt-6 flex flex-wrap gap-4 justify-center">
                    {/* Primary Action Button */}
                    <Button variant="solid" onClick={this.handlePrimaryAction}>
                        {primaryActionLabel ||
                            (appError?.type === 'server' ||
                            appError?.type === 'network'
                                ? 'Try Again'
                                : appError?.type === 'auth'
                                  ? 'Go to Login'
                                  : 'Go to Dashboard')}
                    </Button>

                    {/* Secondary Action - only shown if a secondary label or path is provided */}
                    {(secondaryActionLabel ||
                        this.props.secondaryActionPath) && (
                        <Button
                            variant="solid"
                            onClick={this.handleSecondaryAction}
                        >
                            {secondaryActionLabel || 'Go to Login'}
                        </Button>
                    )}

                    {/* Refresh for server errors */}
                    {statusCode === 500 && !primaryActionLabel && (
                        <Button variant="solid" onClick={this.handleRefresh}>
                            Refresh Page
                        </Button>
                    )}

                    {/* Go Back - can be hidden with showGoBack=false */}
                    {showGoBack && (
                        <Button variant="plain" onClick={this.handleGoBack}>
                            Go Back
                        </Button>
                    )}
                </div>
            </div>
        )
    }

    public render() {
        if (this.state.hasError) {
            // Show custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Show built-in error UI
            return this.renderErrorContent()
        }

        return this.props.children
    }
}

export default function ErrorBoundary(props: ErrorBoundaryProps) {
    const navigate = useNavigate()
    return <ErrorBoundaryInner {...props} navigate={navigate} />
}

// Re-export ErrorBoundaryWrapper for convenience
export { default as ErrorBoundaryWrapper } from './ErrorBoundryWrapper/ErrorBoundaryWrapper'
export type { NavigationOptions } from './ErrorBoundryWrapper/ErrorBoundaryWrapper'
export { NAVIGATION_CONFIGS } from './ErrorBoundryWrapper/ErrorBoundaryWrapper'
